import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight, Edit2, Trash2, Package, X, Image as ImageIcon,
  Plus, LoaderCircle, AlertCircle, CheckCircle, Save,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { useProducts } from "../../context/ProductContext";
import { productService } from "../../services/productService";
import { cloudinaryService } from "../../services/cloudinaryService";
import { CATEGORIES, getCategory } from "../../constants/categories";

const FALLBACK_IMAGE =
  "https://placehold.co/600x400/f5f3f0/78716c?text=Image+indisponible";
const MAX_IMAGES = 5;

const formatPrice = (price) =>
  `${Number(price || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} MAD`;

const inputClass =
  "w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-900 outline-none transition placeholder:text-stone-400 focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10";
const labelClass = "mb-2 block text-sm font-semibold text-stone-700";

// Transforme un produit Firestore en état de formulaire
const productToForm = (product) => {
  const variants =
    Array.isArray(product.variants) && product.variants.length > 0
      ? product.variants.map((v, i) => ({
          id: v.id || `v${i + 1}`,
          label: v.label || "",
          price: v.price ?? "",
          stock: v.stock ?? "",
        }))
      : [
          {
            id: "v1",
            label: product.volume || "Standard",
            price: product.price ?? "",
            stock: "",
          },
        ];

  const images = [
    ...new Set(
      [product.mainImage, ...(product.images || []), product.image].filter(Boolean)
    ),
  ];

  return {
    name: product.name || "",
    sous_description: product.sous_description || "",
    description: product.description || "",
    category: product.category || "",
    variants,
    images,              // URLs existantes
    newFiles: [],        // nouvelles images à uploader : [{ id, file, url }]
  };
};

export default function Products() {
  const { role } = useAuth();
  const {
    products, loading, error, success,
    fetchProducts, clearError, clearSuccess,
  } = useProducts();

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [localError, setLocalError] = useState("");
  const [localSuccess, setLocalSuccess] = useState("");

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const selectedCat = editForm ? getCategory(editForm.category) : null;

  // ── Supprimer ────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) return;

    setDeletingId(id);
    setLocalError("");
    setLocalSuccess("");

    try {
      await productService.delete(id);
      await fetchProducts();
      setLocalSuccess("Produit supprimé avec succès.");
    } catch (err) {
      setLocalError(err.message || "Impossible de supprimer ce produit.");
    } finally {
      setDeletingId(null);
    }
  };

  // ── Ouvrir / fermer modal ────────────────────────────────
  const handleEditOpen = (product) => {
    setEditingId(product.id);
    setEditForm(productToForm(product));
    setUploadProgress(0);
    setLocalError("");
    setLocalSuccess("");
  };

  const handleEditClose = () => {
    if (editLoading) return;
    editForm?.newFiles.forEach((f) => URL.revokeObjectURL(f.url));
    setEditingId(null);
    setEditForm(null);
  };

  // ── Champs texte ─────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  // ── Catégorie : on garde les variantes, seul le libellé change ──
  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setEditForm((prev) => ({ ...prev, category: value }));
  };

  // ── Variantes ────────────────────────────────────────────
  const updateVariant = (index, field, value) =>
    setEditForm((prev) => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === index ? { ...v, [field]: value } : v
      ),
    }));

  const addVariant = () =>
    setEditForm((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        { id: `v${Date.now()}`, label: "", price: "", stock: "" },
      ],
    }));

  const removeVariant = (index) =>
    setEditForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));

  // ── Images ───────────────────────────────────────────────
  const totalImages = editForm
    ? editForm.images.length + editForm.newFiles.length
    : 0;

  const removeExistingImage = (url) =>
    setEditForm((prev) => ({
      ...prev,
      images: prev.images.filter((img) => img !== url),
    }));

  const removeNewFile = (id) =>
    setEditForm((prev) => {
      const target = prev.newFiles.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return { ...prev, newFiles: prev.newFiles.filter((f) => f.id !== id) };
    });

  const handleFileSelect = (e) => {
    const available = MAX_IMAGES - totalImages;
    const files = Array.from(e.target.files).slice(0, Math.max(available, 0));
    e.target.value = "";

    if (files.length === 0) {
      setLocalError(`Maximum ${MAX_IMAGES} images par produit.`);
      return;
    }

    setEditForm((prev) => ({
      ...prev,
      newFiles: [
        ...prev.newFiles,
        ...files.map((file) => ({
          id: Math.random().toString(36).slice(2),
          file,
          url: URL.createObjectURL(file),
        })),
      ],
    }));
  };

  // ── Enregistrer ──────────────────────────────────────────
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingId || !editForm) return;

    const cleanVariants = editForm.variants
      .filter((v) => String(v.label).trim() !== "" || String(v.price).trim() !== "")
      .map((v, i) => ({
        id: v.id || `v${i + 1}`,
        label: String(v.label).trim(),
        price: parseFloat(v.price),
        stock: parseInt(v.stock, 10) || 0,
      }));

    if (cleanVariants.length === 0) {
      setLocalError("Ajoutez au moins une variante avec un prix.");
      return;
    }
    if (cleanVariants.some((v) => !v.label || !Number.isFinite(v.price) || v.price <= 0)) {
      setLocalError("Chaque variante doit avoir un libellé et un prix valide.");
      return;
    }
    if (editForm.images.length + editForm.newFiles.length === 0) {
      setLocalError("Le produit doit avoir au moins une image.");
      return;
    }

    setEditLoading(true);
    setLocalError("");
    setLocalSuccess("");
    setUploadProgress(0);

    try {
      // 1. Upload des nouvelles images
      let newUrls = [];
      if (editForm.newFiles.length > 0) {
        newUrls = await cloudinaryService.uploadMultiple(
          editForm.newFiles.map((f) => f.file),
          (p) => setUploadProgress(Math.round(p))
        );
      }

      // 2. Images finales (anciennes restantes + nouvelles)
      const images = [...editForm.images, ...newUrls].slice(0, MAX_IMAGES);

      // 3. Mise à jour Firestore
      await productService.update(editingId, {
        name: editForm.name.trim(),
        sous_description: editForm.sous_description.trim(),
        description: editForm.description.trim(),
        category: editForm.category,
        variantType: getCategory(editForm.category)?.variantType || "",
        variants: cleanVariants,
        images,
        mainImage: images[0],
      });

      await fetchProducts();

      editForm.newFiles.forEach((f) => URL.revokeObjectURL(f.url));
      setEditingId(null);
      setEditForm(null);
      setLocalSuccess("Produit mis à jour avec succès.");
    } catch (err) {
      setLocalError(err.message || "Impossible de modifier ce produit.");
    } finally {
      setEditLoading(false);
    }
  };

  const displayedError = localError || error;
  const displayedSuccess = localSuccess || success;

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-8">

        {/* En-tête */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-violet-600">
              Administration
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl">
              Gestion des produits
            </h1>
            <p className="mt-2 text-sm text-stone-500">
              Consulte, modifie et gère le catalogue de ta boutique.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-3 shadow-sm">
              <Package size={19} className="text-violet-600" />
              <span className="text-sm font-semibold text-stone-700">
                {products.length} produit{products.length !== 1 ? "s" : ""}
              </span>
            </div>

            {role === "admin" && (
              <Link
                to="/admin/product/add"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700"
              >
                <Plus size={17} />
                <span className="hidden sm:inline">Ajouter un produit</span>
                <span className="sm:hidden">Ajouter</span>
                <ChevronRight size={16} />
              </Link>
            )}
          </div>
        </div>

        {/* Messages */}
        {displayedError && (
          <div role="alert" className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle size={19} className="mt-0.5 shrink-0" />
            <p className="flex-1">{displayedError}</p>
            <button type="button" aria-label="Fermer"
              onClick={() => { setLocalError(""); clearError?.(); }}
              className="rounded-lg p-1 hover:bg-red-100">
              <X size={17} />
            </button>
          </div>
        )}

        {displayedSuccess && (
          <div role="status" className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
            <CheckCircle size={19} className="mt-0.5 shrink-0" />
            <p className="flex-1">{displayedSuccess}</p>
            <button type="button" aria-label="Fermer"
              onClick={() => { setLocalSuccess(""); clearSuccess?.(); }}
              className="rounded-lg p-1 hover:bg-emerald-100">
              <X size={17} />
            </button>
          </div>
        )}

        {/* Chargement */}
        {loading && (
          <div className="flex items-center justify-center gap-3 rounded-2xl border border-stone-200 bg-white py-12 text-sm text-stone-500">
            <LoaderCircle size={22} className="animate-spin text-violet-600" />
            Chargement des produits...
          </div>
        )}

        {/* Vide */}
        {!loading && products.length === 0 && (
          <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-6 py-16 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-stone-100">
              <Package size={27} className="text-stone-400" />
            </div>
            <h2 className="text-lg font-semibold text-stone-800">Aucun produit disponible</h2>
            <p className="mt-2 text-sm text-stone-500">
              Les produits ajoutés à ta boutique apparaîtront ici.
            </p>
          </div>
        )}

        {/* Grille */}
        {!loading && products.length > 0 && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {products.map((product) => (
              <article
                key={product.id}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-stone-200/80 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="relative h-52 overflow-hidden bg-stone-100">
                  <img
                    src={product.mainImage || product.image || FALLBACK_IMAGE}
                    alt={product.name || "Produit"}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = FALLBACK_IMAGE;
                    }}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full border border-white/80 bg-white/95 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-700 shadow-sm">
                    {getCategory(product.category)?.label || product.category || "Sans catégorie"}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-5">
                  <div className="flex-1">
                    <h2 className="line-clamp-2 text-lg font-bold text-stone-900">
                      {product.name || "Produit sans nom"}
                    </h2>
                    {product.sous_description && (
                      <p className="mt-1 line-clamp-1 text-xs text-stone-500">
                        {product.sous_description}
                      </p>
                    )}
                    <p className="mt-3 line-clamp-2 text-sm leading-6 text-stone-500">
                      {product.description || "Aucune description disponible."}
                    </p>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <p className="text-xl font-bold tracking-tight text-violet-700">
                        {product.variants?.length > 1 && "À partir de "}
                        {formatPrice(product.price)}
                      </p>
                      {product.variants?.length > 0 && (
                        <span className="rounded-md bg-stone-100 px-2 py-1 text-[11px] font-semibold text-stone-600">
                          {product.variants.length} variante{product.variants.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>

                  {role === "admin" && (
                    <div className="mt-5 flex gap-3 border-t border-stone-100 pt-4">
                      <button
                        type="button"
                        onClick={() => handleEditOpen(product)}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-amber-500 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
                      >
                        <Edit2 size={15} /> Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(product.id)}
                        disabled={deletingId === product.id}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:opacity-50"
                      >
                        {deletingId === product.id
                          ? <LoaderCircle size={15} className="animate-spin" />
                          : <Trash2 size={15} />}
                        {deletingId === product.id ? "Suppression..." : "Supprimer"}
                      </button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* ═══════════════ MODAL MODIFICATION ═══════════════ */}
        {editingId && editForm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-stone-950/60 p-4 backdrop-blur-sm"
            onMouseDown={(e) => { if (e.target === e.currentTarget) handleEditClose(); }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="edit-product-title"
              className="my-auto flex max-h-[92vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl"
            >
              {/* En-tête */}
              <div className="flex items-center justify-between border-b border-stone-100 px-6 py-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-violet-600">Catalogue</p>
                  <h2 id="edit-product-title" className="mt-1 text-xl font-bold text-stone-900">
                    Modifier le produit
                  </h2>
                </div>
                <button type="button" onClick={handleEditClose} disabled={editLoading}
                  aria-label="Fermer la fenêtre"
                  className="rounded-xl p-2 text-stone-500 hover:bg-stone-100 disabled:opacity-50">
                  <X size={21} />
                </button>
              </div>

              <form onSubmit={handleEditSubmit} className="flex-1 space-y-6 overflow-y-auto p-6">

                {/* Nom */}
                <div>
                  <label htmlFor="p-name" className={labelClass}>Nom *</label>
                  <input id="p-name" name="name" value={editForm.name} onChange={handleChange}
                    type="text" required maxLength={150} className={inputClass} />
                </div>

                {/* Sous-description */}
                <div>
                  <label htmlFor="p-sub" className={labelClass}>Sous-description</label>
                  <input id="p-sub" name="sous_description" value={editForm.sous_description}
                    onChange={handleChange} type="text" maxLength={200} className={inputClass}
                    placeholder="Ex: Protection SPF 50 · Légère et non grasse" />
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="p-desc" className={labelClass}>Description *</label>
                  <textarea id="p-desc" name="description" value={editForm.description}
                    onChange={handleChange} rows={4} required maxLength={3000}
                    className={`${inputClass} resize-y leading-6`} />
                </div>

                {/* Catégorie */}
                <div>
                  <label htmlFor="p-cat" className={labelClass}>Catégorie *</label>
                  <select id="p-cat" name="category" value={editForm.category}
                    onChange={handleCategoryChange} required className={inputClass}>
                    <option value="" disabled>-- Choisir une catégorie --</option>
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                {/* Variantes */}
                {selectedCat && (
                  <div className="rounded-xl bg-stone-50 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-sm font-bold text-stone-800">
                        {selectedCat.variantLabel} et prix *
                      </h3>
                      <span className="text-xs text-stone-500">
                        Prix affiché : {formatPrice(
                          Math.min(...editForm.variants.map((v) => Number(v.price) || Infinity))
                        ) !== "Infinity MAD" ? "à partir de le moins cher" : "—"}
                      </span>
                    </div>

                    <div className="space-y-2">
                      {editForm.variants.map((v, i) => (
                        <div key={v.id || i} className="grid grid-cols-[1fr_120px_90px_auto] gap-2">
                          <input className={inputClass} placeholder={selectedCat.placeholder}
                            value={v.label} onChange={(e) => updateVariant(i, "label", e.target.value)} />
                          <input className={inputClass} type="number" min="0" step="0.01"
                            placeholder="Prix" value={v.price}
                            onChange={(e) => updateVariant(i, "price", e.target.value)} />
                          <input className={inputClass} type="number" min="0" placeholder="Stock"
                            value={v.stock} onChange={(e) => updateVariant(i, "stock", e.target.value)} />
                          <button type="button" disabled={editForm.variants.length === 1}
                            onClick={() => removeVariant(i)} aria-label="Supprimer la variante"
                            className="flex items-center justify-center rounded-xl border border-red-200 px-3 text-red-600 hover:bg-red-50 disabled:opacity-30">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                    </div>

                    <button type="button" onClick={addVariant}
                      className="mt-3 inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-3 py-2 text-xs font-semibold text-stone-700 hover:bg-stone-100">
                      <Plus size={14} /> Ajouter une {selectedCat.variantLabel.toLowerCase()}
                    </button>
                  </div>
                )}

                {/* Images existantes */}
                <div>
                  <p className={`${labelClass} flex items-center justify-between`}>
                    <span className="flex items-center gap-2"><ImageIcon size={16} /> Images</span>
                    <span className="text-xs font-normal text-stone-500">{totalImages}/{MAX_IMAGES}</span>
                  </p>

                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                    {editForm.images.map((url, i) => (
                      <div key={url} className="relative aspect-square overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
                        <img src={url} alt={`Image ${i + 1}`}
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMAGE; }}
                          className="h-full w-full object-cover" />
                        {i === 0 && (
                          <span className="absolute bottom-1 left-1 rounded bg-violet-600 px-1.5 py-0.5 text-[9px] font-bold text-white">
                            PRINCIPALE
                          </span>
                        )}
                        <button type="button" onClick={() => removeExistingImage(url)}
                          aria-label="Supprimer l'image"
                          className="absolute right-1 top-1 rounded-md bg-black/70 p-1 text-white hover:bg-black">
                          <X size={13} />
                        </button>
                      </div>
                    ))}

                    {editForm.newFiles.map((f) => (
                      <div key={f.id} className="relative aspect-square overflow-hidden rounded-xl border-2 border-dashed border-violet-400 bg-stone-50">
                        <img src={f.url} alt="Nouvelle image" className="h-full w-full object-cover" />
                        <span className="absolute bottom-1 left-1 rounded bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold text-white">
                          NOUVELLE
                        </span>
                        <button type="button" onClick={() => removeNewFile(f.id)}
                          aria-label="Retirer la nouvelle image"
                          className="absolute right-1 top-1 rounded-md bg-black/70 p-1 text-white hover:bg-black">
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {totalImages < MAX_IMAGES && (
                    <input type="file" multiple accept="image/*" onChange={handleFileSelect}
                      disabled={editLoading}
                      className="mt-3 block w-full text-sm text-stone-600 file:mr-3 file:rounded-lg file:border-0 file:bg-violet-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-violet-700" />
                  )}
                  <p className="mt-2 text-xs text-stone-500">
                    La première image est l'image principale. Les nouvelles images seront envoyées sur Cloudinary à l'enregistrement.
                  </p>
                </div>

                {/* Progression upload */}
                {editLoading && uploadProgress > 0 && (
                  <div>
                    <div className="h-2 overflow-hidden rounded-full bg-stone-200">
                      <div className="h-full bg-violet-600 transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="mt-1 text-xs text-stone-500">Upload des images : {uploadProgress}%</p>
                  </div>
                )}

                {/* Boutons */}
                <div className="flex flex-col-reverse gap-3 border-t border-stone-100 pt-5 sm:flex-row">
                  <button type="button" onClick={handleEditClose} disabled={editLoading}
                    className="flex-1 rounded-xl border border-stone-200 px-4 py-3 text-sm font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50">
                    Annuler
                  </button>
                  <button type="submit" disabled={editLoading}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 disabled:opacity-60">
                    {editLoading
                      ? <><LoaderCircle size={17} className="animate-spin" /> Enregistrement...</>
                      : <><Save size={17} /> Enregistrer les modifications</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}